import { useCallback } from 'react';

import {
  setSharedFeatureFlag,
  useFeatureFlag,
  useIsOnlinePlannerAllowedForModel,
  useROSControl,
  useRoutineRunnerHandle,
  useSharedFeatureFlag,
} from '@sbrc/hooks';

// Three questions about the deprecated ROS2 API, each with its own source:
// - useOnlinePlannerRunsOnThisRobot(): live flags. Keeping the API would block
//   motion on this robot.
// - useLegacyRosApiBlocksMotion() (warning-widget): botman's cannot-start
//   verdict. Motion is blocked right now.
// - useControlSourceManagerRestartPending() (service-restart): live flag vs
//   botman's effective flag. A restart is still needed.

export const ROS2_API_DEPRECATION_TITLE = 'The ROS2 API is deprecated';

/** Move-page copy while botman reports the block. */
export const LEGACY_ROS_API_BLOCKS_MOTION_MESSAGE =
  'Jogging and routine execution are unavailable while "Keep using the ' +
  'deprecated ROS2 API" is on. Turn it off under Settings → External control ' +
  '→ Developer API, then restart services.';

export const ROS2_API_DEPRECATION_NOTICE =
  '"Enable ROS2 arm control" and the /api/v1/movement/ros/state endpoint ' +
  'will be removed in an upcoming release. Move integrations to the External ' +
  'Control streaming API.';

export const KEEP_LEGACY_ROS_API_BLOCKS_MOTION_NOTICE =
  'While "Keep using the deprecated ROS2 API" is on, jogging and routine ' +
  'execution are unavailable on this robot.';

/**
 * Live flags: whether keeping the deprecated API would block motion here.
 * Botman's rule minus the manager flag, which is what the switch toggles.
 */
export function useOnlinePlannerRunsOnThisRobot(): boolean {
  const trajectoryController = useFeatureFlag('useTrajectoryController');
  const plannerAllowedOnModel = useIsOnlinePlannerAllowedForModel();

  return trajectoryController && plannerAllowedOnModel;
}

export const KEEP_LEGACY_ROS_API_SWITCH_TITLE =
  'Keep using the deprecated ROS2 API';

export const KEEP_LEGACY_ROS_API_SWITCH_DESCRIPTION =
  '"Enable ROS2 arm control" hands the arm to your ROS2 publisher as soon as ' +
  'it is on, so the arm must be ready to move, and it turns off whenever the ' +
  'arm stops. Turn off "Keep using the deprecated ROS2 API" to switch this ' +
  'robot to the External Control streaming API. Services restart when this ' +
  'changes.';

// Turning this on puts the arm into its moving state under ROS2 control right
// away; the robot refuses while the arm is not ready and drops it on every stop.
export const ROS_CONTROL_SWITCH_TITLE = 'Enable ROS2 arm control';

export const ROS_CONTROL_SWITCH_STATUS_DEPRECATED = 'Deprecated';

export const ROS_CONTROL_SWITCH_STATUS_PENDING = 'Waiting for robot';

export const ROS_CONTROL_UPDATE_FAILED_MESSAGE =
  'Failed to change ROS2 arm control.';

export const ROS_CONTROL_SWITCH_DESCRIPTION =
  'Hands the arm to your ROS2 publisher as soon as it is on. The arm must be ' +
  'ready to move, and control ends whenever the arm stops.';

export const ROS_CONTROL_SWITCH_HIDDEN_DESCRIPTION =
  'The deprecated ROS2 API is turned off on this robot. Turn "Keep using the ' +
  'deprecated ROS2 API" on to bring back "Enable ROS2 arm control".';

export const ROS2_BRIDGE_SWITCH_TITLE = 'Enable ROS2 Bridge';

export const ROS2_BRIDGE_SWITCH_DESCRIPTION =
  '"Enable ROS2 Bridge" publishes ROS2 topics on the local network.';

export const ROS2_API_RESTART_BANNER_TITLE =
  'Changing the ROS2 API setting requires a service restart to take effect.';

/**
 * The customer switch is the inverse of the control-source-manager flag.
 * Null until the shared flags have loaded; the per-user hook would report an
 * unloaded flag as off and show the wrong state for the first render.
 */
export function useKeepLegacyRosApi(): boolean | null {
  const controlSourceManager = useSharedFeatureFlag('useControlSourceManager');

  return controlSourceManager === null ? null : !controlSourceManager;
}

/** The switch owns exactly one flag; everything else stays as configured. */
async function writeKeepLegacyRosApi(keepLegacy: boolean): Promise<void> {
  await setSharedFeatureFlag('useControlSourceManager', !keepLegacy);
}

/**
 * Ends ROS2 arm control before the flag is written: arm-control-bot reloads
 * the flag live and ignores the disable once it is on.
 */
export function useSetKeepLegacyRosApi(): (
  keepLegacy: boolean,
) => Promise<void> {
  const routineRunnerHandle = useRoutineRunnerHandle({ isVizbot: false });
  const setRosControlEnabled = useROSControl();

  return useCallback(
    async (keepLegacy: boolean) => {
      if (
        !keepLegacy &&
        routineRunnerHandle.getState()?.kinematicState.rosControl
      ) {
        await setRosControlEnabled(false);
      }

      await writeKeepLegacyRosApi(keepLegacy);
    },
    [routineRunnerHandle, setRosControlEnabled],
  );
}
